import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { useSocket, SOCKET_STATE } from '~Components/SocketManager';
import { useStorage } from '~Components/Storage';
import { useAdyen, useDispatch, useLogger, useSelector } from '~Hooks';
import BaemingoLogo from '~Assets/Logos/baemingo_logo_white.svg';

type PaymentState = 'idle' | 'paying' | 'success' | 'failed';

interface PaymentRequest {
  type: 'START_PAYMENT_REQUEST';
  serviceId: string;
  amount: number;
  transactionId: string;
  terminalId: string;
}

/**
 * ManagedPayments scene - runs inside an Adyen terminal's Android webview.
 *
 * Sits idle showing the Baemingo logo and a websocket status indicator.
 * When a POS sends a START_PAYMENT_REQUEST via websocket, this scene
 * initiates the payment locally on 127.0.0.1 using the useAdyen hook.
 */
const ManagedPayments: React.FunctionComponent = () => {
  const logger = useLogger('managed-payments');
  const socket = useSocket();
  const [storage] = useStorage();
  const adyen = useAdyen();
  const dispatch = useDispatch();

  const [paymentState, setPaymentState] = useState<PaymentState>('idle');
  const [lastAmount, setLastAmount] = useState<number>(0);
  const isProcessing = useRef(false);

  // Get the terminal ID from the first configured terminal
  const terminalId = storage.adyenTerminals?.[0]?.POIID;

  // Register as a managed terminal when the socket opens
  useEffect(() => {
    if (socket.socketState !== SOCKET_STATE.OPEN || !terminalId) {
      return;
    }

    logger.info('Registering as managed terminal', { terminalId });
    socket.send.registerManagedTerminal(terminalId);
  }, [socket.socketState, terminalId]);

  // Re-register every 10 minutes to prevent TTL expiry
  useEffect(() => {
    if (socket.socketState !== SOCKET_STATE.OPEN || !terminalId) {
      return;
    }

    const interval = setInterval(() => {
      logger.info('Re-registering managed terminal', { terminalId });
      socket.send.registerManagedTerminal(terminalId);
    }, 1000 * 60 * 10);

    return () => clearInterval(interval);
  }, [socket.socketState, terminalId]);

  // Listen for START_PAYMENT_REQUEST from Redux
  const paymentRequest = useSelector((state) => {
    const queue = state.startPaymentRequestQueue;
    return queue?.[0] || null;
  });

  const handlePayment = useCallback(
    async (request: PaymentRequest) => {
      if (isProcessing.current) {
        logger.warn('Already processing a payment, ignoring request', {
          serviceId: request.serviceId,
        });
        return;
      }

      isProcessing.current = true;
      setPaymentState('paying');
      setLastAmount(request.amount);

      // Remove from queue so we don't reprocess it
      dispatch({ type: 'REMOVE_START_PAYMENT_REQUEST', serviceId: request.serviceId });

      logger.info('Starting managed payment', {
        serviceId: request.serviceId,
        amount: request.amount,
        transactionId: request.transactionId,
      });

      try {
        const result = await adyen.pay(terminalId!, {
          amount: request.amount,
          transactionId: request.transactionId,
          serviceId: request.serviceId,
          saleId: 'managed',
        });

        logger.info('Managed payment completed', {
          serviceId: request.serviceId,
          tip: result.tip,
          transactionId: result.transactionId,
        });

        setPaymentState('success');

        // Return to idle after the success animation
        setTimeout(() => {
          setPaymentState('idle');
          isProcessing.current = false;
        }, 4000);
      } catch (reason) {
        logger.warn('Managed payment failed', {
          serviceId: request.serviceId,
          reason,
        });

        setPaymentState('failed');

        // Return to idle after showing the failure state
        setTimeout(() => {
          setPaymentState('idle');
          isProcessing.current = false;
        }, 3000);
      }
    },
    [terminalId, adyen],
  );

  // Process payment requests from the queue
  useEffect(() => {
    if (!paymentRequest || isProcessing.current) {
      return;
    }

    handlePayment(paymentRequest);
  }, [paymentRequest, handlePayment]);

  const isConnected = socket.socketState === SOCKET_STATE.OPEN;

  return (
    <Container>
      <Content>
        {paymentState === 'idle' && (
          <>
            <LogoWrapper>
              <Logo src={BaemingoLogo} alt="Baemingo" />
            </LogoWrapper>
            <StatusDot connected={isConnected} />
          </>
        )}

        {paymentState === 'paying' && (
          <PayingState>
            <PulsingDot />
            <PayingText>Processing payment...</PayingText>
            <AmountText>{(lastAmount / 100).toFixed(2)}</AmountText>
          </PayingState>
        )}

        {paymentState === 'success' && (
          <SuccessState>
            <Checkmark viewBox="0 0 52 52">
              <circle cx="26" cy="26" r="25" fill="none" />
              <path fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </Checkmark>
            <SuccessText>Payment successful</SuccessText>
          </SuccessState>
        )}

        {paymentState === 'failed' && (
          <FailedState>
            <FailedIcon>!</FailedIcon>
            <FailedText>Payment failed</FailedText>
          </FailedState>
        )}
      </Content>
    </Container>
  );
};

export default ManagedPayments;

// -- Styled Components --

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0a0a0f;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;

const LogoWrapper = styled.div`
  opacity: 0.85;
`;

const Logo = styled.img`
  width: 200px;
  height: auto;
`;

const StatusDot = styled.div<{ connected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 24px;
  background-color: ${(props) => (props.connected ? '#22c55e' : '#ef4444')};
  transition: background-color 0.3s ease;
  box-shadow: 0 0 8px ${(props) => (props.connected ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)')};
`;

// -- Paying State --

const PayingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const PulsingDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #6366f1;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.5);
      opacity: 0.5;
    }
  }

  animation: pulse 1.5s ease-in-out infinite;
`;

const PayingText = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  font-weight: 300;
  letter-spacing: 0.5px;
`;

const AmountText = styled.span`
  color: rgba(255, 255, 255, 0.4);
  font-size: 14px;
  font-weight: 300;
`;

// -- Success State --

const SuccessState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  animation: fadeInUp 0.4s ease-out;
`;

const Checkmark = styled.svg`
  width: 64px;
  height: 64px;
  stroke: #22c55e;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;

  circle {
    stroke-dasharray: 166;
    stroke-dashoffset: 166;
    animation: stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards;
  }

  path {
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    animation: stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.4s forwards;
  }

  @keyframes stroke {
    100% {
      stroke-dashoffset: 0;
    }
  }
`;

const SuccessText = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 20px;
  font-weight: 300;
  letter-spacing: 0.5px;
`;

// -- Failed State --

const FailedState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  animation: fadeInUp 0.4s ease-out;
`;

const FailedIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  border: 2px solid #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444;
  font-size: 32px;
  font-weight: 300;
`;

const FailedText = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 20px;
  font-weight: 300;
  letter-spacing: 0.5px;
`;
